/* eslint-disable no-promise-executor-return */
import {
  QueryClient,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "react-toastify";
import instance from "services/axiosServices";
import whatsappApi from "./axiosWhatsApp";
import { ContatoWA } from "./types";

const BASE_PATH = "whatsapp/common/whatsapp";

// --- Gate centralizado para invalidação de ["contatos-wa"] ---
// O hub do WhatsApp emite muitos eventos em rajada (mensagens, status, pings),
// e várias mutations (enviar/editar/excluir) também invalidam contatos. Sem
// gate, o React Query cancelava request anterior e abria nova em loop. Usamos
// aqui debounce + cooldown + checagem de fetchStatus para garantir no máximo
// 1 refetch por janela de tempo.
const CONTATOS_INVALIDATE_COOLDOWN_MS = 3_000;
const CONTATOS_INVALIDATE_DEBOUNCE_MS = 800;
const contatosLastRun = new WeakMap<QueryClient, number>();
const contatosTimer = new WeakMap<QueryClient, ReturnType<typeof setTimeout>>();

function runContatosInvalidate(qc: QueryClient) {
  contatosLastRun.set(qc, Date.now());
  qc.invalidateQueries({ queryKey: ["contatos-wa"] });
}

export function invalidateContatosWA(qc: QueryClient) {
  const state = qc.getQueryState(["contatos-wa"]);
  const isFetching = state?.fetchStatus === "fetching";

  const lastRun = contatosLastRun.get(qc) ?? 0;
  const elapsed = Date.now() - lastRun;

  const existing = contatosTimer.get(qc);
  if (existing) clearTimeout(existing);

  if (isFetching || elapsed < CONTATOS_INVALIDATE_COOLDOWN_MS) {
    const wait = Math.max(
      CONTATOS_INVALIDATE_DEBOUNCE_MS,
      CONTATOS_INVALIDATE_COOLDOWN_MS - elapsed
    );
    const timer = setTimeout(() => {
      contatosTimer.delete(qc);
      runContatosInvalidate(qc);
    }, wait);
    contatosTimer.set(qc, timer);
    return;
  }

  const timer = setTimeout(() => {
    contatosTimer.delete(qc);
    runContatosInvalidate(qc);
  }, CONTATOS_INVALIDATE_DEBOUNCE_MS);
  contatosTimer.set(qc, timer);
}

function normalizeContactMessageDate(value: any): string {
  const text = String(value || "").trim();
  if (!text) return "";

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/.test(text)) {
    return text.replace(/Z$/, "");
  }

  return text;
}

function getLastMessageDate(c: ContatoWA): string {
  return normalizeContactMessageDate(
    c.ultimaAtividade ||
      (c as any).ultimaMensagemData ||
      (c as any).ultimaMensagemEm ||
      (c as any).dataUltimaMensagem ||
      (c as any).dataCriacaoUltimaMensagem ||
      ""
  );
}

// Deduplica apenas linhas verdadeiramente repetidas (mesmo rowKey).
// Mantém múltiplos contatos do mesmo leadCorretorId quando têm rowKey/
// telefone diferentes — eles devem aparecer como cards separados.
function dedupeContatos(contatos: ContatoWA[]): ContatoWA[] {
  const seen = new Set<string>();
  const out: ContatoWA[] = [];
  contatos.forEach((c) => {
    const dedupeKey =
      c.rowKey || c.id || `${c.leadCorretorId || ""}::${c.telefone || ""}`;
    if (!dedupeKey || seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    out.push(c);
  });
  return out;
}

export interface ContatosWAV2Result {
  contatos: ContatoWA[];
  quantidadeFiltrosAplicados: number;
}

function normalizeContatos(contatos: ContatoWA[]): ContatoWA[] {
  return contatos.map((c) => {
    const id = c.id || c.rowKey || "";
    const ultimaAtividade = getLastMessageDate(c);
    const viewedAt = localStorage.getItem(`wa_conv_viewed_${id}`);
    const viewedAfterLast =
      viewedAt &&
      ultimaAtividade &&
      new Date(ultimaAtividade) <= new Date(viewedAt);
    return {
      ...c,
      id,
      ultimaAtividade,
      unreadCount: viewedAfterLast ? 0 : c.unreadCount || 0,
    };
  });
}

export function useContatosWAV2(
  body: Record<string, any>,
  palavrasChave = "",
  enabled = true
) {
  return useQuery<ContatosWAV2Result>({
    queryKey: ["contatos-wa-v2", body, palavrasChave],
    queryFn: async ({ queryKey }) => {
      const [, requestBody, keyword] = queryKey as [
        string,
        Record<string, any>,
        string
      ];
      const payload = { ...requestBody };
      if (keyword) {
        payload.PalavrasChave = keyword;
      } else {
        delete payload.PalavrasChave;
      }

      const response = await instance.post(`${BASE_PATH}/contatos/v2`, payload);
      const contatos: ContatoWA[] = response.data?.data?.contatos ?? [];
      return {
        contatos: dedupeContatos(normalizeContatos(contatos)),
        quantidadeFiltrosAplicados:
          response.data?.data?.quantidadeFiltrosAplicados ?? 0,
      };
    },
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: false,
    onError: (error: any) => {
      const notifications = error?.response?.data?.notifications;
      const errorMessages = Array.isArray(notifications)
        ? notifications.map((item: any) => item?.message).join("\n")
        : "Erro ao buscar contatos do WhatsApp";
      toast.error(errorMessages, { autoClose: 10000 });
    },
  });
}

export function useContatosWA() {
  return useQuery<ContatoWA[]>({
    queryKey: ["contatos-wa"],
    queryFn: async () => {
      const response = await instance.get(`${BASE_PATH}/contatos/v1`);
      // API retorna { success, data: { contatos: [...] } }
      const contatos: ContatoWA[] = response.data?.data?.contatos ?? [];

      return normalizeContatos(contatos);
    },
    refetchOnWindowFocus: false,
    // Aplica dedup a CADA leitura do cache (não só no fetch). Garante que
    // mesmo após setQueryData manipular a lista (envio de mensagem, eventos
    // do hub), consumidores recebem sempre lista sem duplicatas de lead.
    select: dedupeContatos,
    onError: (error: any) => {
      const notifications = error?.response?.data?.notifications;
      const errorMessages = Array.isArray(notifications)
        ? notifications.map((item: any) => item?.message).join("\n")
        : "Erro ao buscar contatos do WhatsApp";
      toast.error(errorMessages, { autoClose: 10000 });
    },
  });
}

export function useConverterLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string;
      nome?: string;
      email?: string;
      empreendimento?: string;
      fotoUrl?: string;
    }) => whatsappApi.post(`/whatsapp/contatos/${id}/lead`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contatos-wa"] });
      qc.invalidateQueries({ queryKey: ["wa-leads"] });
    },
  });
}

export function useDescartarContato() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      whatsappApi.delete(`/whatsapp/contatos/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["contatos-wa"] }),
  });
}

export function useImportarChats() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      whatsappApi.post<{ importados: number; total: number }>(
        "/whatsapp/contatos/importar-chats"
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["contatos-wa"] }),
  });
}
